import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  BookOnline as BookOnlineIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

const resolveInvoiceUrl = (booking, download = false) => {
  const baseUrl = booking.invoiceUrl || booking.invoice?.pdfUrl || booking.invoicePDF || booking.invoice?.url || '';

  if (!baseUrl) {
    return '';
  }

  if (!download) {
    return baseUrl;
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}download=1`;
};

const UserBookings = () => {
  const { currentUser } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    totalSpent: 0
  });

  useEffect(() => {
    fetchUserBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchUserBookings = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const q = query(
        collection(db, 'bookings'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const bookingsData = [];
      let totalSpent = 0;
      let completedCount = 0;
      
      querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        bookingsData.push(data);
        
        if (data.status?.toLowerCase() === 'completed') {
          completedCount++;
          totalSpent += parseFloat(data.price || data.totalAmount || 0);
        }
      });
      
      setBookings(bookingsData);
      setStats({
        total: bookingsData.length,
        completed: completedCount,
        totalSpent: totalSpent
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateField) => {
    if (!dateField) return 'N/A';
    if (dateField.toDate && typeof dateField.toDate === 'function') {
      return dateField.toDate().toLocaleDateString('en-AU', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    }
    if (dateField instanceof Date) {
      return dateField.toLocaleDateString('en-AU', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    }
    if (typeof dateField === 'string') {
      try {
        return new Date(dateField).toLocaleDateString('en-AU', { 
          weekday: 'short', 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        });
      } catch {
        return dateField;
      }
    }
    // Support RN-style date map objects: { day, month, year }
    if (
      typeof dateField === 'object' &&
      dateField !== null &&
      dateField.day !== undefined &&
      dateField.month !== undefined &&
      (dateField.year !== undefined || dateField.Y !== undefined)
    ) {
      const day = String(dateField.day).padStart(2, '0');
      const month = String(dateField.month).padStart(2, '0');
      const year = String(dateField.year || dateField.Y);
      return `${day}/${month}/${year}`;
    }
    return 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1.75rem' }}>
          My Bookings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and update your bookings here
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ px: 4, pb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: 'none',
              border: '1px solid #e8e8e8'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#E3F2FD', 
                    borderRadius: 2, 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BookOnlineIcon sx={{ color: '#1976d2', fontSize: 30 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                      Total Bookings
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                      {stats.total}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: 'none',
              border: '1px solid #e8e8e8'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#E8F5E9', 
                    borderRadius: 2, 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <TrendingUpIcon sx={{ color: '#2e7d32', fontSize: 30 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                      Completed
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                      {stats.completed}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: 'none',
              border: '1px solid #e8e8e8'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#F3E5F5', 
                    borderRadius: 2, 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ReceiptIcon sx={{ color: '#7b1fa2', fontSize: 30 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                      Total Spent
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                      ${stats.totalSpent.toFixed(0)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Bookings Table */}
      <Box sx={{ px: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8' }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Booking #</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Services</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Actions</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666' }}>Invoices</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No bookings found. Book a service to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        #{booking.orderNumber || booking.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.serviceCount || 1} service{(booking.serviceCount || 1) > 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{ width: 32, height: 32 }}
                          src={booking.serviceImage || ''}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {booking.serviceName || booking.serviceTitle || 'Service'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.category || 'General'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(booking.scheduledDate || booking.bookingDate || booking.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        ${parseFloat(booking.price || booking.totalAmount || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status || 'Pending'}
                        color={getStatusColor(booking.status)}
                        size="small"
                        icon={booking.status?.toLowerCase() === 'completed' ? 
                          <span style={{ fontSize: '16px' }}>✓</span> : undefined
                        }
                        sx={{ 
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {booking.status?.toLowerCase() === 'completed' && booking.hasInvoice ? (
                        (() => {
                          const invoiceUrl = resolveInvoiceUrl(booking);
                          const invoiceDownloadUrl = resolveInvoiceUrl(booking, true);

                          if (!invoiceUrl) {
                            return (
                              <Typography variant="caption" color="text.secondary">
                                Invoice recorded
                              </Typography>
                            );
                          }

                          return (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                component="a"
                                href={invoiceUrl}
                                target="_blank"
                                rel="noreferrer"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                View
                              </Button>
                              <Button
                                component="a"
                                href={invoiceDownloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                download
                                size="small"
                                startIcon={<DownloadIcon />}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                Download
                              </Button>
                            </Box>
                          );
                        })()
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ReceiptIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                          <Typography variant="caption" color="text.secondary">
                            No invoice
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default UserBookings;
